type NativeHttpResponse = {
  data?: string | null;
  status?: number;
  headers?: Record<string, string>;
};

type CapacitorBridge = {
  isNativePlatform?: () => boolean;
  nativePromise?: (
    plugin: string,
    method: string,
    options: Record<string, unknown>,
  ) => Promise<NativeHttpResponse>;
};

export type NativeBlobResult = {
  status: number;
  blob: Blob;
  /** Error body as text when the status is not OK. */
  errorText: string | null;
};

function bridge(): CapacitorBridge | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as { Capacitor?: CapacitorBridge }).Capacitor;
}

export function nativeHttpAvailable(): boolean {
  const cap = bridge();
  return cap?.isNativePlatform?.() === true && typeof cap.nativePromise === "function";
}

export function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function headerValue(headers: Record<string, string> | undefined, name: string): string {
  if (!headers) return "";
  const wanted = name.toLowerCase();
  for (const [key, value] of Object.entries(headers)) {
    if (key.toLowerCase() === wanted) return value;
  }
  return "";
}

/**
 * Capacitor's patched `fetch` reads every body as text. PDF bytes are not UTF-8,
 * so `response.blob()` is empty. Ask the native HTTP plugin for base64 instead.
 */
export async function requestNativeBlob(
  url: string,
  method: string,
  headers: Headers,
): Promise<NativeBlobResult> {
  const cap = bridge();
  if (!cap?.nativePromise) {
    throw new Error("Không gọi được HTTP native.");
  }
  const headerObj: Record<string, string> = {};
  headers.forEach((value, key) => {
    headerObj[key] = value;
  });
  const native = await cap.nativePromise("CapacitorHttp", "request", {
    url,
    method,
    headers: headerObj,
    responseType: "blob",
  });
  const status = native.status ?? 0;
  const raw = typeof native.data === "string" ? native.data : "";
  if (status < 200 || status >= 300) {
    let errorText = raw;
    try {
      errorText = new TextDecoder().decode(base64ToBytes(raw));
    } catch {
      errorText = raw;
    }
    return { status, blob: new Blob(), errorText };
  }
  const type = headerValue(native.headers, "content-type").split(";")[0] || "application/octet-stream";
  const bytes = base64ToBytes(raw);
  return {
    status,
    blob: new Blob([bytes], { type }),
    errorText: null,
  };
}
